import {
    TWO_WAY_AUTH_CHECK_IF_VALIDATED_ROUTE,
    postTwoWayAuthResendOtpRoute,
    TWO_WAY_AUTH_VALIDATE_OTP_ROUTE,
} from "@api-routes";
import { useFetch } from "@common/hooks/useFetch";
import { ROUTES } from "@constants";
import { fixUrlLanguage, getLanguage } from "@utils/changeLanguage";
import { useState } from "react";
import { useHistory } from "react-router-dom";
import { useTwoWayAuthContext } from "src/context/TwoWayAuthContext";
import { keycloakUtils } from "src/itsf/keycloak/keycloakUtils";

export interface IResendOtpResponse {
    errorCode: string;
    expires_in: number;
    hasError: boolean;
}

const defaultResendValue: IResendOtpResponse = {
    errorCode: "",
    expires_in: 0,
    hasError: false,
};

export const useTwoWayAuth = () => {
    const [resendOtpState, { post: postResendOtp }] = useFetch();
    const [validateOtpState, { post: postValidateOtp }] = useFetch();
    const [checkIfValidatedState, { get: getIfValidated }] = useFetch();
    const { setIsTwoWayAuthenticated } = useTwoWayAuthContext();
    const lang = getLanguage();
    const [resendResponse, setResendResponse] = useState<IResendOtpResponse>(defaultResendValue);
    const [isOtpValid, setIsOtpValid] = useState<boolean>(true);
    const history = useHistory();

    const onValidationSuccess = () => {
        setIsTwoWayAuthenticated(true);
        history.push(fixUrlLanguage(ROUTES.HOME_PAGE_URL_EN));
    };

    const resetToDefault = () => {
        setResendResponse(defaultResendValue);
        setIsOtpValid(true);
    };

    const handleResendOtp = async () => {
        resetToDefault();

        await postResendOtp<IResendOtpResponse>(postTwoWayAuthResendOtpRoute(lang), {
            isSecured: keycloakUtils.isAuthenticated(),
        })
            .then((response: IResendOtpResponse) => {
                setResendResponse(response);
            })
            .catch((err: any) => {
                setResendResponse(defaultResendValue);
                console.log("handleResendOtp error: ", err);
            });
    };

    const handleValidateOtp = (otp: string) => {
        resetToDefault();

        postValidateOtp<boolean>(TWO_WAY_AUTH_VALIDATE_OTP_ROUTE, {
            body: {
                otp: otp,
            },
            isSecured: keycloakUtils.isAuthenticated(),
        })
            .then((response: boolean) => {
                response ? onValidationSuccess() : setIsOtpValid(false);
            })
            .catch((err: any) => {
                setIsOtpValid(false);
                console.log("handleValidateOtp error: ", err);
            });
    };

    const handleCheckIfValidated = async () => {
        await getIfValidated<boolean>(TWO_WAY_AUTH_CHECK_IF_VALIDATED_ROUTE)
            .then(async (response: boolean) => {
                setIsTwoWayAuthenticated(response);
                if (!response) {
                    await handleResendOtp();
                    history.push(fixUrlLanguage(ROUTES.TWO_WAY_AUTH_OTP_URL_EN));
                }
            })
            .catch((err: any) => {
                setIsTwoWayAuthenticated(false);
                history.push(fixUrlLanguage(ROUTES.TWO_WAY_AUTH_OTP_URL_EN));
                console.log("handleCheckIfValidated error: ", err);
            });
    };

    return {
        handleResendOtp,
        resendOtpState,
        handleValidateOtp,
        validateOtpState,
        checkIfValidatedState,
        handleCheckIfValidated,
        resendResponse,
        isOtpValid,
    };
};
